import { create } from 'zustand';

import { toPlayerMessage } from '@/shared/api';
import { isFresh, singleFlight } from '@/shared/lib';

import {
  fetchMyPredictions,
  fetchPopup,
  markPopupSeen,
} from '../api/predictions.api';

import type { Prediction, PredictionPopup } from './types';

/**
 * Прогнозы игрока. Баланса Фанкоинов здесь нет: он приходит сводкой
 * в срез сессии, откуда его читает шапка. Иначе одно и то же число
 * лежало бы в двух сторах и расходилось.
 */
interface PredictionsState {
  items: Prediction[];
  isLoading: boolean;
  error: string | null;
  loadedAt: number | null;

  /**
   * Итоги, которые игрок ещё не видел. `null` — показывать нечего;
   * `popupLoadedAt` отличает это от «мы ещё не спрашивали».
   */
  popup: PredictionPopup | null;
  popupLoadedAt: number | null;

  load: (options?: { force?: boolean }) => Promise<void>;
  loadPopup: () => Promise<void>;
  /** Закрыть поп-ап и сжечь его на сервере. */
  dismissPopup: () => void;
  /** Локальное применение ответа `POST /predictions` без лишнего запроса. */
  applyCreated: (prediction: Prediction) => void;
  reset: () => void;
}

export const usePredictionsStore = create<PredictionsState>()((set, get) => ({
  items: [],
  isLoading: false,
  error: null,
  loadedAt: null,
  popup: null,
  popupLoadedAt: null,

  load: singleFlight(async (options?: { force?: boolean }): Promise<void> => {
    if (!options?.force && isFresh(get().loadedAt)) return;

    set({ isLoading: true, error: null });
    try {
      set({ items: await fetchMyPredictions(), loadedAt: Date.now() });
    } catch (error) {
      set({ error: toPlayerMessage(error) });
    } finally {
      set({ isLoading: false });
    }
  }),

  loadPopup: singleFlight(async (): Promise<void> => {
    if (isFresh(get().popupLoadedAt)) return;

    try {
      const popup = await fetchPopup();
      set({
        popup: popup.hasResults ? popup : null,
        popupLoadedAt: Date.now(),
      });
    } catch {
      // Молча: итоги — приятный бонус, а не содержимое экрана. Красная
      // плашка вместо поздравления хуже, чем отсутствие поздравления.
      set({ popupLoadedAt: Date.now() });
    }
  }),

  /**
   * Оптимистично: сначала убираем модалку, потом сообщаем серверу.
   *
   * Если POST не дойдёт, игрок увидит те же итоги на следующем заходе —
   * это правильная деградация. Держать модалку открытой из-за сетевой
   * ошибки нельзя: закрыть её игрок не сможет.
   */
  dismissPopup: () => {
    set({ popup: null });
    void markPopupSeen().catch(() => undefined);
  },

  applyCreated: (prediction) => {
    set({ items: [...get().items, prediction] });
  },

  reset: () => {
    set({
      items: [],
      error: null,
      loadedAt: null,
      popup: null,
      popupLoadedAt: null,
    });
  },
}));
